import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Alert, Box, CircularProgress, Container, Paper, TextField, Typography } from '@mui/material';
import { FormRecord, fetchFormByUuid } from '../utils/formsApi';

const formatValue = (value: unknown): string => {
  if (value === undefined || value === null) {
    return '';
  }

  return typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value);
};

const FormDetailsPage: React.FC = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const [form, setForm] = useState<FormRecord | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    if (!uuid) {
      setError('A form UUID is required.');
      setIsLoading(false);
      return () => {
        isMounted = false;
      };
    }

    fetchFormByUuid(uuid)
      .then((selectedForm) => {
        if (isMounted) {
          setForm(selectedForm);
        }
      })
      .catch((requestError) => {
        console.error('Failed to fetch form details:', requestError);
        if (isMounted) {
          setError('Failed to load the form details.');
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [uuid]);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4, textAlign: 'left' }}>
      <Typography component={Link} to="/forms" color="primary" sx={{ textDecoration: 'none' }}>
        ← Available Forms
      </Typography>
      <Typography variant="h4" sx={{ mt: 2, mb: 3 }}>Form Details</Typography>

      {error && <Alert severity="error">{error}</Alert>}
      {isLoading && <CircularProgress aria-label="Loading form details" />}

      {form && (
        <Paper sx={{ p: 3 }}>
          <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {Object.entries(form).map(([field, value]) => {
              const displayValue = formatValue(value);
              const isStructuredValue = typeof value === 'object' && value !== null;

              return (
                <TextField
                  key={field}
                  label={field}
                  value={displayValue}
                  multiline={isStructuredValue || displayValue.length > 120}
                  minRows={isStructuredValue ? 8 : undefined}
                  fullWidth
                  slotProps={{ input: { readOnly: true } }}
                  sx={isStructuredValue ? { '& textarea': { fontFamily: 'monospace', fontSize: '0.8rem' } } : undefined}
                />
              );
            })}
          </Box>
        </Paper>
      )}
    </Container>
  );
};

export default FormDetailsPage;
